import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import SEO from "../../components/SEO";
import { getBlogArticleBySlug, getRelatedArticles } from "../../data/blogData";

const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = getBlogArticleBySlug(slug || "");
  const relatedArticles = getRelatedArticles(slug || "", 3);

  useEffect(() => {
    if (!article) {
      navigate("/blog");
    }
  }, [article, navigate]);

  if (!article) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const parseContent = (content: string) => {
    return content.replace(
      /\*\*(.*?)\*\*/g,
      '<span class="font-primaryMedium text-darkB">$1</span>',
    );
  };

  // Insert content image roughly in the middle of sections
  const midSectionIndex = Math.floor(article.sections.length / 2);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.seo.description,
    image: article.heroImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      "@type": "Person",
      name: article.author,
      jobTitle: "Chirurgien Esthétique et Plastique",
      url: "https://www.dryoussefgam.com/biographie",
    },
    publisher: {
      "@type": "Organization",
      name: "Dr Youssef Gam",
      logo: {
        "@type": "ImageObject",
        url: "https://www.dryoussefgam.com/logo-black.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.seo.canonicalUrl,
    },
    keywords: article.seo.keywords.join(", "),
    articleSection: article.category,
    wordCount: article.sections
      .reduce((acc, section) => acc + section.content.join(" "), "")
      .split(" ").length,
  };

  // FAQ structured data if article has FAQs
  const faqStructuredData = article.faqs
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      }
    : null;

  return (
    <>
      <SEO
        title={article.seo.title}
        description={article.seo.description}
        keywords={article.seo.keywords}
        canonicalUrl={article.seo.canonicalUrl}
        ogImage={article.seo.ogImage}
        ogType="article"
        publishDate={article.publishDate}
        author={article.author}
        structuredData={
          faqStructuredData
            ? [structuredData, faqStructuredData]
            : [structuredData]
        }
      />

      {/* Hero Section */}
      <section className="relative bg-lightViolet pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #000 0.5px, transparent 0)`,
              backgroundSize: "32px 32px",
            }}
          />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
            aria-label="Breadcrumb"
          >
            <ol className="flex items-center gap-2 text-xs text-consent font-primaryLight">
              <li>
                <Link to="/" className="hover:text-darkB transition-colors">
                  Accueil
                </Link>
              </li>
              <li className="text-consent">/</li>
              <li>
                <Link to="/blog" className="hover:text-darkB transition-colors">
                  Blog
                </Link>
              </li>
              <li className="text-consent">/</li>
              <li className="text-darkB font-primaryMedium line-clamp-1">
                {article.title}
              </li>
            </ol>
          </motion.nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            {/* Category & Read Time */}
            <div className="flex items-center gap-3 mb-6">
              <span className="px-3 py-1 bg-darkB text-lightB text-[10px] font-primaryMedium uppercase tracking-[0.2em]">
                {article.category}
              </span>
              <span className="text-consent font-primaryLight text-xs">
                {article.readTime} de lecture
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-primaryMedium text-darkB mb-6 leading-[1.05]">
              {article.title}
            </h1>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "60px" }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="h-[1px] bg-darkB mb-6"
            />
            <p className="text-lg md:text-xl text-gray600 font-primaryLight mb-10 leading-relaxed max-w-3xl">
              {article.subtitle}
            </p>

            {/* Author Info */}
            <div className="flex items-center gap-3 pb-10 border-b border-gray200">
              {article.authorImage ? (
                <img
                  src={article.authorImage}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-darkB flex items-center justify-center text-lightB font-primaryMedium text-xs">
                  YG
                </div>
              )}
              <div className="text-left">
                <p className="text-darkB font-primaryMedium text-sm">
                  {article.author}
                </p>
                <p className="text-consent text-xs font-primaryLight">
                  {formatDate(article.publishDate)}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      {article.heroImage && (
        <section className="py-0 px-4 md:px-8">
          <div className="max-w-5xl mx-auto -mt-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="relative h-[400px] md:h-[500px] overflow-hidden"
            >
              <img
                src={article.heroImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-darkB/10 to-transparent" />
            </motion.div>
          </div>
        </section>
      )}

      {/* Article Content */}
      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-14 pl-6 border-l-2 border-darkB"
          >
            <p className="text-lg text-gray600 font-primaryLight leading-relaxed italic">
              {article.excerpt}
            </p>
          </motion.div>

          {/* Table of Contents */}
          <motion.nav
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-14 p-8 bg-lightViolet"
          >
            <h2 className="text-lg font-primaryMedium text-darkB mb-5 uppercase tracking-wider">
              Sommaire
            </h2>
            <ol className="space-y-2.5">
              {article.sections.map((section, index) => (
                <li key={index}>
                  <a
                    href={`#section-${index}`}
                    className="flex items-start gap-3 text-gray600 hover:text-darkB transition-colors font-primaryLight text-sm group"
                  >
                    <span className="flex-shrink-0 w-6 text-xs text-consent font-primaryMedium mt-0.5">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="group-hover:translate-x-1 transition-transform">
                      {section.title}
                    </span>
                  </a>
                </li>
              ))}
              {article.faqs && (
                <li>
                  <a
                    href="#faq"
                    className="flex items-start gap-3 text-gray600 hover:text-darkB transition-colors font-primaryLight text-sm group"
                  >
                    <span className="flex-shrink-0 w-6 text-xs text-consent font-primaryMedium mt-0.5">
                      FAQ
                    </span>
                    <span className="group-hover:translate-x-1 transition-transform">
                      Questions fréquentes
                    </span>
                  </a>
                </li>
              )}
            </ol>
          </motion.nav>

          {/* Sections */}
          {article.sections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <motion.section
                id={`section-${sectionIndex}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="mb-14 scroll-mt-24"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-xs font-primaryMedium text-consent tracking-wider">
                    {String(sectionIndex + 1).padStart(2, "0")}
                  </span>
                  <div className="w-8 h-[1px] bg-consent" />
                </div>
                <h2 className="text-2xl md:text-3xl font-primaryMedium text-darkB leading-snug mb-6">
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-gray600 font-primaryLight leading-[1.8] text-base"
                      dangerouslySetInnerHTML={{
                        __html: parseContent(paragraph),
                      }}
                    />
                  ))}
                </div>
              </motion.section>

              {/* Content Image - inserted after the middle section */}
              {sectionIndex === midSectionIndex && article.contentImage && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="mb-14 -mx-4 md:-mx-12"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={article.contentImage}
                      alt={`${article.title} - illustration`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-darkB/10 to-transparent" />
                  </div>
                </motion.div>
              )}
            </div>
          ))}

          {/* FAQs */}
          {article.faqs && article.faqs.length > 0 && (
            <motion.section
              id="faq"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-14 scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-8">
                <span className="text-xs font-primaryMedium text-consent tracking-wider">
                  FAQ
                </span>
                <div className="w-8 h-[1px] bg-consent" />
              </div>
              <h2 className="text-2xl md:text-3xl font-primaryMedium text-darkB mb-10">
                Questions fréquentes
              </h2>
              <div className="space-y-6">
                {article.faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="p-6 bg-lightViolet border-l-2 border-darkB"
                  >
                    <h3 className="text-lg font-primaryMedium text-darkB mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-gray600 font-primaryLight leading-relaxed text-sm">
                      {faq.answer}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Tags */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center gap-2 py-8 border-t border-gray200"
          >
            <span className="text-consent font-primaryMedium uppercase text-[10px] tracking-wider mr-2">
              Mots-clés
            </span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-lightViolet text-gray600 text-xs font-primaryLight hover:bg-darkB hover:text-lightB transition-colors cursor-pointer"
              >
                {tag}
              </span>
            ))}
          </motion.div>

          {/* Author Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="my-14 p-8 bg-lightViolet"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              {article.authorImage ? (
                <img
                  src={article.authorImage}
                  alt={article.author}
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-darkB flex items-center justify-center text-lightB font-primaryMedium text-lg flex-shrink-0">
                  YG
                </div>
              )}
              <div>
                <h3 className="text-xl font-primaryMedium text-darkB mb-2">
                  {article.author}
                </h3>
                <p className="text-gray600 font-primaryLight text-sm mb-3 leading-relaxed">
                  Chirurgien Esthétique et Plastique reconnu en Tunisie,
                  spécialiste des techniques innovantes en médecine et chirurgie
                  esthétique.
                </p>
                <Link
                  to="/biographie"
                  className="inline-flex items-center gap-2 text-darkB font-primaryMedium hover:opacity-70 transition-opacity uppercase text-[10px] tracking-wider"
                >
                  Voir la biographie
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center py-14 px-8 border border-darkB"
          >
            <h3 className="text-2xl md:text-3xl font-primaryMedium text-darkB mb-4">
              Intéressé par cette intervention ?
            </h3>
            <p className="text-gray600 font-primaryLight mb-8 max-w-lg mx-auto leading-relaxed">
              Prenez rendez-vous pour une consultation personnalisée avec le Dr.
              Youssef Gam.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3.5 bg-darkB text-lightB font-primaryMedium uppercase text-xs tracking-wider hover:opacity-90 transition-opacity"
              >
                Prendre rendez-vous
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-20 px-4 md:px-8 bg-lightViolet">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-3 mb-12"
            >
              <h2 className="text-3xl font-primaryMedium text-darkB">
                Articles similaires
              </h2>
              <div className="flex-1 h-[1px] bg-gray200" />
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle, index) => (
                <motion.article
                  key={relatedArticle.id}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/blog/${relatedArticle.slug}`}>
                    <div className="bg-lightB transition-all duration-500 border border-transparent hover:border-gray200">
                      <div className="relative h-52 overflow-hidden bg-lightViolet">
                        {relatedArticle.thumbnailImage ? (
                          <motion.img
                            src={relatedArticle.thumbnailImage}
                            alt={relatedArticle.title}
                            className="w-full h-full object-cover"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.5 }}
                          />
                        ) : (
                          <div className="w-full h-full bg-lightViolet" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-darkB/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-lightB/90 text-darkB text-[10px] font-primaryMedium uppercase tracking-wider backdrop-blur-sm">
                          {relatedArticle.category}
                        </span>
                      </div>
                      <div className="p-5">
                        <h3 className="text-lg font-primaryMedium text-darkB mb-3 group-hover:opacity-70 transition-opacity line-clamp-2 leading-snug">
                          {relatedArticle.title}
                        </h3>
                        <p className="text-gray600 font-primaryLight text-sm leading-relaxed line-clamp-2 mb-4">
                          {relatedArticle.excerpt}
                        </p>
                        <div className="flex items-center gap-2 text-darkB font-primaryMedium text-[10px] uppercase tracking-wider group-hover:gap-3 transition-all">
                          <span>Lire l'article</span>
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <div className="py-10 text-center bg-lightB">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-darkB font-primaryMedium hover:opacity-70 transition-opacity uppercase text-[10px] tracking-wider"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16l-4-4m0 0l4-4m-4 4h18"
            />
          </svg>
          Retour au blog
        </Link>
      </div>
    </>
  );
};

export default BlogArticlePage;
